import {Component, ElementRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {MatSelect, MatSelectChange} from "@angular/material/select";

/**
 * Improved version of custom-select supporting value clearing.
 *
 * Clearing affordances (enabled via [clearable]):
 *  - trash icon shown inside the (closed) trigger when a value is selected
 *  - "Clear" button in the sticky footer of the opened panel
 *  - the search bar "x" only clears the search text (not the selected value)
 *
 * The cleared value is defined by [clearValue] (default: null).
 */
@Component({
	selector: 'lc-custom-select-v1',
	templateUrl: './lc-custom-select-v1.component.html',
	styleUrls: ['./lc-custom-select-v1.component.scss']
})
export class LcCustomSelectV1Component implements OnInit, OnChanges
{
	@Input()
	label: String | undefined;

	@Input()
	placeholder: String | undefined;

	@Input()
	values?: any[];

	@Input()
	defaultValue: any;

	@Input()
	disabled: boolean | undefined = false;

	@Output()
	defaultValueChange: EventEmitter<any> = new EventEmitter<any>();

	@Input()
	columnName?: string;

	@Output()
	columnChange: EventEmitter<string> = new EventEmitter<string>()

	@Input()
	extraClass: string = '';

	@Input()
	searchable: boolean = true;

	@Input()
	useTranslation: boolean = false;

	@Input()
	translationPrefix: string = "";

	/** Enables the clearing affordances (trigger trash icon + footer "Clear" button). */
	@Input()
	clearable: boolean = false;

	/** Value assigned/emitted when the selection is cleared. */
	@Input()
	clearValue: any = null;

	/** When true (default), the panel closes after clearing; when false it stays open. */
	@Input()
	closeOnClear: boolean = true;

	/** When true, auto-selects the first available value if none is set. Opt-in. */
	@Input()
	autoSelectFirst: boolean = false;

	searchValue: string = ""; //The ID rendered are filtered by this value

	filterValues: any[] | undefined; //Contains the filter values on selector

	@ViewChild('selectElement') selectElement: MatSelect | undefined;
	@ViewChild('searchInput') searchInput: ElementRef | undefined;
	@ViewChild('optionsScroll') optionsScroll: ElementRef | undefined;

	constructor()
	{
	}

	ngOnInit(): void
	{
		if (this.autoSelectFirst && this.isEmptyValue(this.defaultValue) && this.values?.length)
		{
			//no value is present, but there is a list of possible values, then choose the 1st
			this.selectValue(this.values[0].id);
		}
	}

	ngOnChanges(changes: SimpleChanges)
	{
		if (changes["values"])
		{
			const current = changes["values"].currentValue ?? [];

			if (this.searchValue)
			{
				this.filterValues = [...current].filter(option => this.matchesSearch(option, this.searchValue));
			}
			else
			{
				this.filterValues = [...current];
			}
		}
	}

	onChange(event: MatSelectChange): void
	{
		//The internal [(ngModel)] already keeps defaultValue (the id) in sync
		this.defaultValueChange.emit(event.value);
		this.columnChange.emit(this.columnName ?? '');
	}

	onSearchValueChange(event: any)
	{
		if (event)
		{
			this.filterValues = this.values?.filter(option => this.matchesSearch(option, event));
		}
		else
		{
			this.searchValue = "";
			this.filterValues = this.values;
		}
	}

	openedChange(event: any)
	{
		if (event)
		{
			//Reset the search on every open
			this.searchValue = "";
			this.filterValues = this.values;

			//Scroll the options list (not the whole panel) to the top
			let scroller = this.optionsScroll?.nativeElement as HTMLDivElement;
			if (scroller)
			{
				scroller.scrollTop = 0;
			}

			//Give focus to the input so the user can search immediately
			this.searchInput?.nativeElement.focus();
		}
	}

	/** Clears the currently selected value. */
	clear(event?: Event): void
	{
		if (event)
		{
			//Prevent the panel from toggling when clearing from the closed trigger
			event.stopPropagation();
			event.preventDefault();
		}

		this.defaultValue = this.clearValue;
		this.defaultValueChange.emit(this.clearValue);
		this.columnChange.emit(this.columnName ?? '');

		this.searchValue = "";
		this.filterValues = this.values;

		if (this.closeOnClear)
		{
			this.selectElement?.close();
		}
	}

	get hasValue(): boolean
	{
		return !this.isEmptyValue(this.defaultValue);
	}

	trackByOptionId(index: number, option: any): any
	{
		return option?.id ?? index;
	}

	private selectValue(id: any): void
	{
		this.defaultValue = id;
		this.defaultValueChange.emit(id);
		this.columnChange.emit(this.columnName ?? '');
	}

	private isEmptyValue(value: any): boolean
	{
		return value === null || value === undefined || value === '';
	}

	private matchesSearch(option: any, search: string): boolean
	{
		const term = String(search ?? '').toUpperCase();
		const id = String(option?.id ?? '').toUpperCase();
		const name = String(option?.name ?? '').toUpperCase();
		return id.includes(term) || name.includes(term);
	}
}
